import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';
import { Modal, Tabs, Tab } from 'react-bootstrap';
import Button from '../../../components/common/Button';
import Loader from '../../../components/common/Loader';
import MoveStepper from './MoveStepper';
import DestinationSelect from './DestinationSelect';
import CandidatesTable from './CandidatesTable';
import {
  splitByTab,
  toggleCandidate,
  toggleAllInTab,
  getTabSelectionState,
  reconcileSelection,
  selectedCandidates,
} from '../../utils/moveSelection';
import { getMoveErrorI18nKey } from '../../utils/moveErrors';
import './MoveCandidatesModal.css';

const STEP_DESTINATION = 1;
const STEP_SELECTION = 2;
const STEP_CONFIRMATION = 3;
const STEP_RESULT = 4;

const initialState = {
  step: STEP_DESTINATION,
  destination: null,
  selection: [],
  activeTab: 'all',
  limitReached: false,
};

class MoveCandidatesModal extends Component {
  static defaultProps;

  state = { ...initialState };

  componentDidMount() {
    if (this.props.show) this.loadDestinations();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) this.loadDestinations();
    if (prevProps.movePreview !== this.props.movePreview) {
      this.handlePreviewChange();
    }
  }

  loadDestinations() {
    this.props.getMoveDestinations(this.props.sourceCampaign.id);
  }

  requestPreview(destination = this.state.destination) {
    this.props.getMovePreview(this.props.sourceCampaign.id, destination.id);
  }

  // The preview of the selected destination, or null while it is not there
  // yet (a late response for a previous destination must never be used).
  getPreview() {
    const { movePreview } = this.props;
    const { destination } = this.state;
    const matches =
      movePreview &&
      destination &&
      movePreview.destination &&
      movePreview.destination.id === destination.id;
    return matches ? movePreview : null;
  }

  getMaxSelectable(preview) {
    return preview ? preview.destination.available_credits : 0;
  }

  handlePreviewChange() {
    const { movePreview, movePreviewStatus } = this.props;
    const { destination, step, selection } = this.state;
    if (!movePreview || !movePreviewStatus.success || !destination) return;

    const preview = this.getPreview();
    if (preview) {
      const reconciled = reconcileSelection(
        selection,
        preview.candidates,
        this.getMaxSelectable(preview),
      );
      this.setState({ selection: reconciled.selection, limitReached: false });
    } else if (step === STEP_SELECTION) {
      // A response for a previous destination clobbered the current one.
      this.requestPreview();
    }
  }

  handleClose = () => {
    this.setState({ ...initialState });
    this.props.resetMoveFlow();
    this.props.onClose();
  };

  handleGoToDestination = (destinationId) => {
    this.props.onGoToDestination(destinationId);
    this.setState({ ...initialState });
    this.props.resetMoveFlow();
  };

  goToStep = (step) => this.setState({ step });

  handleDestinationChange = (destination) => this.setState({ destination });

  handleNext = () => {
    const { step } = this.state;
    if (step === STEP_DESTINATION) {
      this.requestPreview();
      this.goToStep(STEP_SELECTION);
    } else if (step === STEP_SELECTION) {
      this.goToStep(STEP_CONFIRMATION);
    }
  };

  handleBack = () => this.goToStep(this.state.step - 1);

  handleToggle = (candidate) => {
    const preview = this.getPreview();
    const { selection, limitReached } = toggleCandidate(
      this.state.selection,
      candidate,
      this.getMaxSelectable(preview),
    );
    this.setState({ selection, limitReached });
  };

  handleToggleAll = () => {
    const preview = this.getPreview();
    const { selection, limitReached } = toggleAllInTab(
      this.state.selection,
      this.getActiveTabCandidates(preview),
      this.getMaxSelectable(preview),
    );
    this.setState({ selection, limitReached });
  };

  handleConfirm = () => {
    const { sourceCampaign } = this.props;
    const { destination, selection } = this.state;
    this.props.moveCandidates(sourceCampaign.id, destination.id, selection);
  };

  getActiveTabCandidates(preview) {
    if (!preview) return [];
    return splitByTab(preview.candidates)[this.state.activeTab];
  }

  canGoNext(preview) {
    const { step, destination, selection } = this.state;
    if (step === STEP_DESTINATION) {
      return !!destination && destination.available_credits > 0;
    }
    if (!preview || this.props.movePreviewStatus.loading) return false;
    return (
      selection.length > 0 && selection.length <= this.getMaxSelectable(preview)
    );
  }

  renderError(errorKey) {
    return (
      <div className="move-error">{I18n.t(getMoveErrorI18nKey(errorKey))}</div>
    );
  }

  renderLoader() {
    return (
      <div className="move-loader">
        <Loader />
      </div>
    );
  }

  renderCredits(destination) {
    return ` (${I18n.t('campaigns.move.availableCreditsCount', {
      count: destination.available_credits,
    })})`;
  }

  renderChangeButton() {
    return (
      <Button
        id="move-change"
        className="text-link-button"
        text={I18n.t('campaigns.move.change')}
        iconButton
        iconRight
        iconClass="edit_square"
        onClick={() => this.goToStep(STEP_DESTINATION)}
        disabled={this.props.moveCandidatesStatus.loading}
      />
    );
  }

  renderStepDestination() {
    const { moveDestinations, moveDestinationsStatus } = this.props;
    const { destination } = this.state;
    return (
      <div className="move-step move-step-destination">
        <label className="move-field-label">
          {I18n.t('campaigns.move.selectDestination')}
          <span className="required-mark"> *</span>
        </label>
        {moveDestinationsStatus.loading && this.renderLoader()}
        {moveDestinationsStatus.error &&
          this.renderError(this.props.moveDestinationsError)}
        {!moveDestinationsStatus.error && (
          <DestinationSelect
            destinations={moveDestinations}
            value={destination}
            sourceIeActive={Boolean(this.props.sourceCampaign.ie_game_active)}
            loading={moveDestinationsStatus.loading}
            onChange={this.handleDestinationChange}
          />
        )}
      </div>
    );
  }

  renderStepSelection(preview) {
    const { movePreviewStatus, movePreviewError } = this.props;
    const { destination, selection, limitReached } = this.state;
    const max = this.getMaxSelectable(preview);
    const overLimit = !!preview && selection.length > max;

    return (
      <div className="move-step move-step-selection">
        <div className="move-destination-header">
          <p>
            {I18n.t('campaigns.move.movingTo')} <b>{destination.name}</b>
            {this.renderCredits(preview ? preview.destination : destination)}
          </p>
          {this.renderChangeButton()}
        </div>
        {movePreviewStatus.loading && this.renderLoader()}
        {movePreviewStatus.error && this.renderError(movePreviewError)}
        {preview && !movePreviewStatus.loading && (
          <div className="move-selection-body">
            {this.renderTabs(preview)}
            {(limitReached || overLimit) && (
              <div className="move-warning">
                {overLimit
                  ? I18n.t('campaigns.move.overLimit', { count: max })
                  : I18n.t('campaigns.move.creditLimit', { count: max })}
              </div>
            )}
            {preview.candidates.some((c) => c.state === 'playing') && (
              <div className="move-note">
                <span className="material-symbols-rounded">info</span>
                {I18n.t('campaigns.move.playingNote')}
              </div>
            )}
            <div className="move-selection-footer">
              <span className="move-selected-count">
                {I18n.t('campaigns.move.selectedCount', {
                  count: selection.length,
                })}
              </span>
              {selection.length === 0 && (
                <span className="move-hint">
                  {I18n.t('campaigns.move.noSelection')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderTabs(preview) {
    const { selection, activeTab } = this.state;
    const tabs = splitByTab(preview.candidates);
    const tabTitle = (key, list) => `${I18n.t(key)} (${list.length})`;
    const renderTable = (list) => (
      <CandidatesTable
        candidates={list}
        selection={selection}
        selectionState={getTabSelectionState(selection, list)}
        onToggle={this.handleToggle}
        onToggleAll={this.handleToggleAll}
      />
    );
    return (
      <Tabs
        id="move-candidates-tabs"
        className="move-tabs"
        animation={false}
        mountOnEnter
        unmountOnExit
        activeKey={activeTab}
        onSelect={(activeKey) => this.setState({ activeTab: activeKey })}
      >
        <Tab
          eventKey="all"
          title={tabTitle('campaigns.move.tabAll', tabs.all)}
        >
          {renderTable(tabs.all)}
        </Tab>
        <Tab
          eventKey="consumed"
          title={tabTitle('campaigns.move.tabConsumed', tabs.consumed)}
        >
          {renderTable(tabs.consumed)}
        </Tab>
        <Tab
          eventKey="notConsumed"
          title={tabTitle('campaigns.move.tabNotConsumed', tabs.notConsumed)}
        >
          {renderTable(tabs.notConsumed)}
        </Tab>
      </Tabs>
    );
  }

  renderStepConfirmation(preview) {
    const { sourceCampaign, moveCandidatesStatus, moveCandidatesError } =
      this.props;
    const { destination, selection } = this.state;
    const rows = selectedCandidates(
      selection,
      preview ? preview.candidates : [],
    );

    return (
      <div className="move-step move-step-confirmation">
        <div className="move-destination-header">
          <p>
            {I18n.t('campaigns.move.movingCountFrom', {
              count: selection.length,
            })}{' '}
            <b>{sourceCampaign.name}</b>{' '}
            {I18n.t('campaigns.move.movingCountTo')} <b>{destination.name}</b>
            {this.renderCredits(preview ? preview.destination : destination)}
          </p>
          {this.renderChangeButton()}
        </div>
        <CandidatesTable
          candidates={rows}
          selectable={false}
          statusLabelKey="campaigns.move.currentStatus"
        />
        <div className="move-credits-info">
          {I18n.t('campaigns.move.creditsInfo', { count: selection.length })}
        </div>
        <p className="move-confirm-question">
          {I18n.t('campaigns.move.confirmQuestion')}
        </p>
        {moveCandidatesStatus.loading && this.renderLoader()}
        {moveCandidatesStatus.error && this.renderError(moveCandidatesError)}
      </div>
    );
  }

  renderResult() {
    const { moveResult } = this.props;
    const { destination, selection } = this.state;
    const destinationName = moveResult.destination
      ? moveResult.destination.name
      : destination.name;
    const moved =
      typeof moveResult.moved === 'number'
        ? moveResult.moved
        : selection.length;
    return (
      <div className="move-result">
        <span className="result-icon material-symbols-rounded material-symbols-filled">
          check_circle
        </span>
        <h4>{I18n.t('campaigns.move.success')}</h4>
        <p className="move-result-message">
          {I18n.t('campaigns.move.successMessage', {
            count: moved,
            campaign: destinationName,
          })}
        </p>
      </div>
    );
  }

  renderResultFooter() {
    const { moveResult } = this.props;
    const destinationId = moveResult.destination
      ? moveResult.destination.id
      : this.state.destination.id;
    return (
      <Modal.Footer>
        <Button
          id="move-view-source"
          className="secondary"
          text={I18n.t('campaigns.move.viewSource')}
          onClick={this.handleClose}
        />
        <Button
          id="move-go-destination"
          className="primary"
          text={I18n.t('campaigns.move.goToDestination')}
          onClick={() => this.handleGoToDestination(destinationId)}
        />
      </Modal.Footer>
    );
  }

  renderFooter(preview) {
    const { step } = this.state;
    const moving = this.props.moveCandidatesStatus.loading;
    const cancel = (
      <Button
        id="move-cancel"
        className="secondary"
        text={I18n.t('campaigns.move.cancel')}
        onClick={this.handleClose}
        disabled={moving}
      />
    );
    const back = (
      <Button
        id="move-back"
        className="secondary"
        text={I18n.t('campaigns.move.back')}
        iconButton
        iconClass="arrow_back"
        onClick={this.handleBack}
        disabled={moving}
      />
    );
    if (step === STEP_CONFIRMATION) {
      return (
        <Modal.Footer>
          {back}
          <div className="footer-right">
            {cancel}
            <Button
              id="move-confirm"
              className="primary"
              text={I18n.t('campaigns.move.confirm')}
              onClick={this.handleConfirm}
              disabled={moving}
            />
          </div>
        </Modal.Footer>
      );
    }
    const next = (
      <Button
        id="move-next"
        className="primary"
        text={I18n.t('campaigns.move.next')}
        iconButton
        iconRight
        iconClass="arrow_forward"
        onClick={this.handleNext}
        disabled={!this.canGoNext(preview)}
      />
    );
    if (step === STEP_SELECTION) {
      return (
        <Modal.Footer>
          {back}
          {next}
        </Modal.Footer>
      );
    }
    return (
      <Modal.Footer>
        <div className="footer-right">
          {cancel}
          {next}
        </div>
      </Modal.Footer>
    );
  }

  render() {
    const { show, moveCandidatesStatus, moveResult } = this.props;
    const { step } = this.state;
    const preview = this.getPreview();
    const showResult = moveCandidatesStatus.success && !!moveResult;

    return (
      <Modal
        show={show}
        onHide={this.handleClose}
        dialogClassName={`move-candidates-modal step-${
          showResult ? 'result' : step
        }`}
      >
        <Modal.Header closeButton>
          <div className="move-title">
            <span className="title-icon material-symbols-rounded">
              move_group
            </span>
            <Modal.Title>{I18n.t('campaigns.move.title')}</Modal.Title>
            <p className="move-subtitle">{I18n.t('campaigns.move.subtitle')}</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <MoveStepper currentStep={showResult ? STEP_RESULT : step} />
          {showResult ? (
            this.renderResult()
          ) : (
            <React.Fragment>
              {step === STEP_DESTINATION && this.renderStepDestination()}
              {step === STEP_SELECTION &&
                this.state.destination &&
                this.renderStepSelection(preview)}
              {step === STEP_CONFIRMATION &&
                this.state.destination &&
                this.renderStepConfirmation(preview)}
            </React.Fragment>
          )}
        </Modal.Body>
        {showResult ? this.renderResultFooter() : this.renderFooter(preview)}
      </Modal>
    );
  }
}

MoveCandidatesModal.defaultProps = {
  show: false,
  moveDestinations: [],
  moveDestinationsStatus: { loading: false, error: false, success: false },
  movePreviewStatus: { loading: false, error: false, success: false },
  moveCandidatesStatus: { loading: false, error: false, success: false },
  onClose: () => {},
  onGoToDestination: () => {},
};
MoveCandidatesModal.propTypes = {
  show: PropTypes.bool,
  sourceCampaign: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string,
    ie_game_active: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func,
  onGoToDestination: PropTypes.func,
  moveDestinations: PropTypes.array,
  moveDestinationsStatus: PropTypes.object,
  moveDestinationsError: PropTypes.string,
  movePreview: PropTypes.object,
  movePreviewStatus: PropTypes.object,
  movePreviewError: PropTypes.string,
  moveResult: PropTypes.object,
  moveCandidatesStatus: PropTypes.object,
  moveCandidatesError: PropTypes.string,
  getMoveDestinations: PropTypes.func.isRequired,
  getMovePreview: PropTypes.func.isRequired,
  moveCandidates: PropTypes.func.isRequired,
  resetMoveFlow: PropTypes.func.isRequired,
};

export default MoveCandidatesModal;
